/**
 * Identifies a mesh archive well enough for the server to fetch and convert tiles from it
 * (`server/src/routes/mesh.ts`) — either a Trimble Connect file (resolved through the Core
 * API, same as the browser's own `CoreApi.getDownloadUrl`) or a plain fetchable URL (`?mesh=`
 * deep links, the `🔗` picker). Built by `tc/meshArchive.ts`, which has the `CoreApi`
 * dependency this lower-level `io/` module deliberately doesn't.
 */
export type MeshServerArchive =
  | { kind: "connect"; fileId: string; versionId?: string; baseUrl: string; archiveToken?: string }
  | { kind: "url"; url: string };

export interface MeshServerOptions {
  archive: MeshServerArchive;
  /** The caller's own Trimble identity bearer token — verified server-side against
   *  Trimble's identity provider and the whitelist (see `server/src/auth.ts`). Always the
   *  signed-in user's own session token, never a share's scoped one (that's carried
   *  separately, inside `archive.archiveToken`, for resolving the archive's download URL). */
  authToken: () => Promise<string>;
}

/** Which quality tier to request — see `MeshLodLevel` in `server/src/mesh/convertStaged.ts`
 *  for what each level actually contains; kept as a separate, string-identical type here
 *  rather than imported, since `src/` is the browser bundle and must not reach into `server/`. */
export type MeshLodLevel = "preview" | "full";

/**
 * Fetches one tile's server-converted, Draco-compressed binary glTF (`POST /api/mesh/tile`)
 * — converted and cached by the server on first request, streamed straight from cache after
 * (see the project plan's "fastest way to display a textured mesh" pick). Throws on any
 * failure (network, 401/403, conversion error); `MeshLayer.loadTile` falls back to the direct
 * client-side OBJ path when this rejects, so a server hiccup degrades to slow rather than
 * broken.
 */
export async function fetchTileGlb(
  options: MeshServerOptions,
  tilePath: string,
  level: MeshLodLevel,
  signal?: AbortSignal,
): Promise<ArrayBuffer> {
  const token = await options.authToken();
  const archive = options.archive;
  const body =
    archive.kind === "url"
      ? { url: archive.url, tilePath, level }
      : {
          fileId: archive.fileId,
          versionId: archive.versionId,
          baseUrl: archive.baseUrl,
          archiveToken: archive.archiveToken,
          tilePath,
          level,
        };
  const response = await fetch("/api/mesh/tile", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
    signal,
  });
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`Mesh tile conversion failed: HTTP ${response.status}${text ? ` — ${text.slice(0, 200)}` : ""}`);
  }
  return response.arrayBuffer();
}
